// useVoltzSandbox - the console's session, carried by the platform.
//
// URL-Analyzer's console drove its gateway directly: it created a session, held
// one websocket for frames, telemetry and input, and deleted the session to stop.
// On Voltz none of that is the browser's to do. The console submits a URL
// analysis like any other, and the run reaches it through the platform's live
// routes - the screen, the feed and the input route - each authorized against
// the submission's org, each fenced to the run on the provider, and none of them
// a socket that both watches and drives.
//
// What it returns is the console's own Sandbox shape, so the console renders a
// Voltz run exactly as it rendered a gateway session: the same chrome, the same
// telemetry, fed the same messages.

import { type QueryClient, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useReportCapabilities } from '@/components/report/capabilities';
import { type AnalysisInput, type AnalyzerChoice, ApiError, api } from '@/lib/api';
import { readFeedStream } from '@/lib/feed';
import { readScreenStream, type ScreenFrame } from '@/lib/screen';
import type {
  AiState,
  AiVerdict,
  ConnectionState,
  Detection,
  DialogRequest,
  NetSummary,
  PageState,
  QRCode,
  SbxEvent,
  ServerMessage,
  Verdict,
} from './types';

/** Cap retained events so a chatty page cannot grow the tab without bound. */
const MAX_EVENTS = 5000;

/**
 * Kinds whose newest payload IS a report panel, latched outside `events`.
 *
 * `events` is a ring buffer that evicts oldest-first. Preflight lands in the
 * first seconds while wire traffic pushes thousands of events in behind it, so
 * a report recovered by scanning that array scrolls off the front on any busy
 * page and the panel blanks out mid-session. Latching keeps it for the run.
 */
const REPORT_KINDS = new Set([
  'preflight.report',
  'preflight.destinations',
  'preflight.recon',
  'security.headers',
  'discovery.robots_txt',
  'discovery.linked_pages',
  'discovery.wayback',
  'discovery.sitemap',
]);

/** Discovery findings accumulate rather than replace, and must also survive. */
const DISCOVERY_KINDS = new Set(['discovery.url_scanned', 'discovery.url_found']);

/** Discovery is low-volume; this only guards against a pathological crawl. */
const MAX_DISCOVERIES = 2000;

/**
 * How long telemetry is collected before it is handed to React. One render per
 * window however much arrived, and short enough that the panel still reads as
 * live.
 */
const FLUSH_MS = 100;

/**
 * The budget a console run is submitted with.
 *
 * A ceiling on a session somebody is driving, not its expected length. The
 * analyst ends a run with Stop, and the analyzer settles one nobody is watching
 * as soon as its page goes quiet, so an abandoned console does not hold a runner
 * for this long. Inside the analyzer backend's own session lifetime.
 *
 * The report prints it as the budget a stopped run was cut short of, so a
 * ceiling nothing reaches reads as a run that ended early rather than one that
 * finished. It is also what a hung runner is held against: an abandoned console
 * settles on its own, but a run that wedges holds its lane for the whole budget.
 */
const CONSOLE_TIMEOUT_SECONDS = 300;

/**
 * Pause before asking again for a live view that is not up yet, and the ceiling
 * that pause climbs to.
 *
 * A run waits for a free runner, then for an image, then for a browser, and a
 * console opened at submit asks through all of it. Two seconds is right for the
 * common case - a warm runner answers almost at once - and doubling up to the
 * ceiling keeps a console left open on a queued run from asking hundreds of
 * times while it waits.
 */
const RETRY_MS = 2000;
const RETRY_CEILING_MS = 10_000;

/** How often the run's status is read while it is in flight. */
const STATUS_POLL_MS = 2000;

/** Statuses after which nothing about the run changes. */
const SETTLED: ReadonlySet<string> = new Set(['completed', 'failed', 'canceled']);

export interface SessionOptions {
  url?: string;
  userAgent?: string;
  referer?: string;
  nsfwBlur?: boolean;
  /** The country the run's traffic leaves from, when the analyst chose one.
   *  Undefined leaves it to intake, which applies the deployment default. */
  exitCountry?: string;
}

/** Where the run is, in the terms the console needs beyond its connection. */
export type RunPhase = 'idle' | 'queued' | 'running' | 'finishing' | 'complete' | 'failed';

export interface Sandbox {
  connection: ConnectionState;
  /** The submission the console is showing, or null before one exists. */
  runId: string | null;
  phase: RunPhase;
  page: PageState;
  events: SbxEvent[];
  /** Newest payload per report kind, immune to `events` eviction. */
  reports: Record<string, Record<string, unknown>>;
  /** Every discovery finding of the run, likewise immune to eviction. */
  discoveries: SbxEvent[];
  detections: Detection[];
  qrCodes: QRCode[];
  verdict: Verdict;
  score: number;
  /** LLM-owned status + explanation, once the end-of-run pass has produced one. */
  aiVerdict: AiVerdict | null;
  /** Lifecycle of that pass, for the spinner / disabled hint. */
  aiState: AiState;
  /** Technology fingerprint detected from page headers, HTML, and script URLs. */
  technologies: Record<string, string[]>;
  /** Icon filename (from tech database) keyed by technology name. */
  techIcons: Record<string, string>;
  /** End-of-session network telemetry: stats, IPs+ASN, domain graph, certs. */
  netSummary: NetSummary | null;
  dialog: DialogRequest | null;
  error: string | null;
  /** A live view is refused because every one this viewer may hold is open. */
  viewLimited: boolean;
  /** Latest screencast frame, or null before first paint. */
  frame: ImageBitmap | null;
  frameMeta: Record<string, number>;

  start: (opts?: SessionOptions) => Promise<void>;
  /** Follow a run that is already in flight, rather than starting one. */
  attach: (submissionId: string) => void;
  stop: () => Promise<void>;
  /** Stop and clear the console's display state. The tab's close button; the
   *  evidence survives in the submission's report. */
  reset: () => Promise<void>;
  /** Run the AI verdict now, while the run goes on ("Finish & analyse"). */
  finish: () => Promise<void>;
  navigate: (url: string) => void;
  reload: () => void;
  send: (msg: Record<string, unknown>) => void;
  answerDialog: (accept: boolean, promptText?: string) => void;
  /** Where a captured file can be downloaded, or null while the run has no
   *  archive to serve it from. */
  artifactUrl: (sha256: string) => string | null;
  /** Whether this reader may steer the run. False makes stop, finish and
   *  every input a no-op, and the console offers none of them. */
  canDrive: boolean;
}

const EMPTY_PAGE: PageState = { url: null, title: null, loading: false, favicon: null };

/**
 * Where the run is, including the beat the server has not caught up with.
 *
 * `stopRequested` is the OPTIMISTIC first beat of a finish: the request was
 * accepted but the task still reports `running`, because the analyzer is being
 * told to tear the session down. The status is polled every couple of seconds,
 * so without it the console spends that window believing it can still drive a
 * session that has gone - and every pointer move in it is an action the
 * provider refuses and the orchestrator reports as a server fault.
 */
function phaseOf(runId: string | null, status: string | null, stopRequested: boolean): RunPhase {
  if (runId === null) return 'idle';
  if (stopRequested && status !== null && !SETTLED.has(status)) return 'finishing';
  switch (status) {
    case 'running':
      return 'running';
    case 'uploading':
    case 'stopping':
      return 'finishing';
    case 'completed':
      return 'complete';
    case 'failed':
    case 'canceled':
      return 'failed';
    default:
      return 'queued';
  }
}

/** Resolves after ms, or at once when the signal aborts. */
function delay(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    const timer = setTimeout(done, ms);
    function done() {
      clearTimeout(timer);
      signal.removeEventListener('abort', done);
      resolve();
    }
    signal.addEventListener('abort', done, { once: true });
  });
}

/**
 * The analyzer catalog's cache key. The console reads it twice - the start
 * panel for the exit countries it may offer, and `start` for the analyzer to
 * name - and one key keeps that one read.
 */
export const ANALYZER_CATALOG_KEY = ['analyzer-catalog'] as const;

/** The URL analyzer this deployment offers, preferring one with a runner. */
async function chooseAnalyzer(qc: QueryClient): Promise<AnalyzerChoice> {
  const { analyzers } = await qc.fetchQuery({
    queryKey: ANALYZER_CATALOG_KEY,
    queryFn: api.analyzerCatalog,
  });
  const url = analyzers.filter((a) => a.input_kinds.includes('url'));
  const pick = url.find((a) => a.capacity) ?? url[0];
  if (!pick) {
    throw new Error(
      'No URL analyzer is registered. An administrator adds one under Administration → Analyzers.',
    );
  }
  return pick;
}

/**
 * What the analyst chose for this run, in the analyzer's option vocabulary.
 *
 * A choice the analyst made is always sent, and a key the analyzer does not
 * declare is refused by the server rather than dropped - a run that quietly
 * ignored the user agent picked for it still produces a plausible report about
 * a user agent it never used. The NSFW gate is the one setting the console
 * always has a value for, so it is sent when the analyzer takes it, and
 * otherwise only when the analyst switched it away from its default.
 */
function consoleOptions(opts: SessionOptions, declared: string[]): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  if (opts.userAgent) out.user_agent = opts.userAgent;
  if (opts.referer) out.referer = opts.referer;
  if (opts.nsfwBlur !== undefined && (declared.includes('nsfw_blur') || opts.nsfwBlur === false)) {
    out.nsfw_blur = opts.nsfwBlur;
  }
  return out;
}

const MOUSE_KINDS: Record<string, AnalysisInput['kind']> = {
  mouseMoved: 'pointer_move',
  mousePressed: 'pointer_down',
  mouseReleased: 'pointer_up',
  mouseWheel: 'wheel',
};

const BUTTONS: Record<string, AnalysisInput['button']> = {
  none: 'none',
  left: 'left',
  middle: 'middle',
  right: 'right',
  back: 'back',
  forward: 'forward',
};

const KEY_KINDS: Record<string, AnalysisInput['kind']> = {
  keyDown: 'key_down',
  rawKeyDown: 'key_raw_down',
  keyUp: 'key_up',
};

/** Actions that are one deliberate act each, whose failure the analyst needs
 *  told about. A pointer move that did not land is followed by another. */
const DISCRETE: ReadonlySet<string> = new Set(['navigate', 'reload', 'dialog', 'text']);

/**
 * The console's own client message, in the platform's input vocabulary.
 *
 * The console speaks the gateway's websocket dialect; the platform's input
 * route takes a closed vocabulary of its own. Coordinates are rounded because
 * the route takes whole pixels, and a key code outside the virtual-key range
 * - a character with no key of its own - is sent as none, since the character
 * itself travels as the key's text.
 */
function inputOf(msg: Record<string, unknown>): AnalysisInput | null {
  const n = (v: unknown) => (typeof v === 'number' && Number.isFinite(v) ? v : 0);
  const s = (v: unknown) => (typeof v === 'string' ? v : undefined);
  switch (msg.t) {
    case 'mouse': {
      const kind = MOUSE_KINDS[String(msg.event)];
      if (!kind) return null;
      const action: AnalysisInput = {
        kind,
        x: Math.max(0, Math.round(n(msg.x))),
        y: Math.max(0, Math.round(n(msg.y))),
        button: BUTTONS[String(msg.button)] ?? 'none',
        click_count: Math.max(0, Math.min(3, Math.round(n(msg.clickCount)))),
        modifiers: n(msg.modifiers),
      };
      if (kind === 'wheel') {
        action.delta_x = Math.round(n(msg.deltaX));
        action.delta_y = Math.round(n(msg.deltaY));
      }
      return action;
    }
    case 'key': {
      const kind = KEY_KINDS[String(msg.event)];
      if (!kind) return null;
      const keyCode = n(msg.keyCode);
      return {
        kind,
        key: s(msg.key),
        code: s(msg.code),
        key_code: keyCode > 255 ? 0 : keyCode,
        text: kind === 'key_up' ? undefined : s(msg.text),
        modifiers: n(msg.modifiers),
      };
    }
    case 'text':
      return { kind: 'text', text: s(msg.text) };
    case 'navigate':
      return { kind: 'navigate', url: s(msg.url) };
    case 'reload':
      return { kind: 'reload' };
    case 'dialog':
      return { kind: 'dialog', accept: msg.accept === true, prompt_text: s(msg.promptText) ?? '' };
    case 'resize':
      return { kind: 'resize', width: Math.round(n(msg.width)), height: Math.round(n(msg.height)) };
    default:
      return null;
  }
}

function messageOf(err: unknown, fallback: string): string {
  return err instanceof Error && err.message ? err.message : fallback;
}

export function useVoltzSandbox(orgId: string | null | undefined): Sandbox {
  const qc = useQueryClient();
  // A host whose reader may watch but not steer: stop, finish and input all
  // stand down, and the console reads canDrive to offer none of them.
  const { drive: canDrive } = useReportCapabilities();

  const [connection, setConnection] = useState<ConnectionState>('idle');
  const [runId, setRunId] = useState<string | null>(null);
  const [page, setPage] = useState<PageState>(EMPTY_PAGE);
  const [events, setEvents] = useState<SbxEvent[]>([]);
  const [reports, setReports] = useState<Record<string, Record<string, unknown>>>({});
  const [discoveries, setDiscoveries] = useState<SbxEvent[]>([]);
  const [detections, setDetections] = useState<Detection[]>([]);
  const [qrCodes, setQrCodes] = useState<QRCode[]>([]);
  const [verdict, setVerdict] = useState<Verdict>('unknown');
  const [score, setScore] = useState(0);
  const [aiVerdict, setAiVerdict] = useState<AiVerdict | null>(null);
  const [aiState, setAiState] = useState<AiState>('idle');
  const [technologies, setTechnologies] = useState<Record<string, string[]>>({});
  const [techIcons, setTechIcons] = useState<Record<string, string>>({});
  const [netSummary, setNetSummary] = useState<NetSummary | null>(null);
  const [dialog, setDialog] = useState<DialogRequest | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [frame, setFrame] = useState<ImageBitmap | null>(null);
  const [frameMeta, setFrameMeta] = useState<Record<string, number>>({});
  // Whether a live view is refused for the viewer limit, kept per stream since
  // a host may count screens and feeds apart. The ref holds the truth so the
  // hot paths (every frame, every message) set state only when it changes.
  const viewBusyRef = useRef({ screen: false, feed: false });
  const [viewLimited, setViewLimited] = useState(false);
  const markViewBusy = useCallback((view: 'screen' | 'feed', busy: boolean) => {
    if (viewBusyRef.current[view] === busy) return;
    viewBusyRef.current = { ...viewBusyRef.current, [view]: busy };
    setViewLimited(viewBusyRef.current.screen || viewBusyRef.current.feed);
  }, []);

  // The run the console is showing, for callbacks that must not re-create on
  // every status poll.
  const runRef = useRef<string | null>(null);
  const statusRef = useRef<string | null>(null);
  const settledRef = useRef(false);
  // Telemetry that has arrived and not yet been rendered, and the timer that
  // will render it (see flush).
  const pendingRef = useRef<{ events: SbxEvent[]; discoveries: SbxEvent[] } | null>(null);
  const flushTimerRef = useRef<number | null>(null);
  // At most one frame decodes at a time; a newer one arriving meanwhile waits
  // here and replaces any older one still waiting (see paint).
  const decodingRef = useRef(false);
  const waitingFrameRef = useRef<ScreenFrame | null>(null);
  // The streams following the current run: one controller aborts both.
  const streamsRef = useRef<{ runId: string; ctrl: AbortController } | null>(null);
  // Bumped whenever the console starts over, so a frame still decoding or a
  // request still in flight for the previous run cannot land in this one.
  const generationRef = useRef(0);
  // The newest event sequence applied. A feed opens by replaying the run so
  // far, so a reconnect would otherwise add every event twice.
  const lastSeqRef = useRef(0);
  const frameRef = useRef<ImageBitmap | null>(null);
  // Set once the analyst asked the run to end, so the cancellation of a queued
  // run they stopped reads as their own act rather than as a failure.
  const stopRequestedRef = useRef(false);

  const statusQ = useQuery({
    queryKey: ['url-console', 'submission', runId],
    queryFn: () => api.getSubmission(runId as string),
    enabled: runId !== null,
    // Polled while the run can still change, and not after: a settled run has
    // no reason to keep asking.
    refetchInterval: (query) => {
      const s = query.state.data?.task.status;
      return s && SETTLED.has(s) ? false : STATUS_POLL_MS;
    },
    // A detonation does not pause because the analyst switched windows, and
    // this query is what the console's own lifecycle hangs off. Left at the
    // default it stops polling in a background tab, so a run that started,
    // finished and was never seen as running reaches an analyst as a console
    // that showed nothing until they came back to it.
    refetchIntervalInBackground: true,
  });
  const task = statusQ.data?.task;
  const status = task?.status ?? null;
  // State as well as the ref: the ref gates the send path synchronously, and
  // the phase the console renders has to recompute when it flips.
  const [stopRequested, setStopRequested] = useState(false);
  const failureMessage = task?.failure?.message ?? null;
  const liveNow = status === 'running';
  // Nothing about this run can change any more. Everything that opens or
  // retries a live view asks this rather than "is it running", because the two
  // differ for the whole queue wait and this is the one that says when to stop.
  const settled = status !== null && SETTLED.has(status);

  useEffect(() => {
    statusRef.current = status;
    settledRef.current = settled;
  }, [status, settled]);

  useEffect(() => {
    frameRef.current = frame;
  }, [frame]);

  const markConnected = useCallback(() => {
    setConnection((c) => (c === 'starting' ? 'connected' : c));
  }, []);

  /**
   * Collect events and hand them to React in batches.
   *
   * A page that loads fifty resources emits fifty messages inside a few
   * milliseconds, and a state update per message means fifty renders of a
   * panel holding thousands of rows - with a copy of the whole buffer each
   * time. That is what makes the tab stutter exactly while the interesting
   * part of a detonation is happening. The flush is one render per window,
   * whatever arrives in it, and the window is short enough to still read as
   * live.
   */
  const flush = useCallback(() => {
    flushTimerRef.current = null;
    const pending = pendingRef.current;
    if (!pending) return;
    pendingRef.current = null;
    if (pending.events.length > 0) {
      setEvents((prev) => {
        const next = prev.concat(pending.events);
        return next.length > MAX_EVENTS ? next.slice(next.length - MAX_EVENTS) : next;
      });
    }
    if (pending.discoveries.length > 0) {
      setDiscoveries((prev) =>
        prev.length >= MAX_DISCOVERIES
          ? prev
          : prev.concat(pending.discoveries).slice(0, MAX_DISCOVERIES),
      );
    }
  }, []);

  const schedule = useCallback(() => {
    if (flushTimerRef.current === null) {
      flushTimerRef.current = window.setTimeout(flush, FLUSH_MS);
    }
  }, [flush]);

  const buffer = useCallback(
    (entry: SbxEvent) => {
      pendingRef.current ??= { events: [], discoveries: [] };
      pendingRef.current.events.push(entry);
      schedule();
    },
    [schedule],
  );

  const bufferDiscovery = useCallback(
    (entry: SbxEvent) => {
      pendingRef.current ??= { events: [], discoveries: [] };
      pendingRef.current.discoveries.push(entry);
      schedule();
    },
    [schedule],
  );

  const handleMessage = useCallback(
    (msg: ServerMessage) => {
      markConnected();
      switch (msg.t) {
        case 'state':
          setPage((prev) => ({
            url: msg.url !== undefined ? msg.url : prev.url,
            title: msg.title !== undefined ? msg.title : prev.title,
            loading: msg.loading !== undefined ? msg.loading : prev.loading,
            favicon: msg.favicon !== undefined ? msg.favicon : prev.favicon,
          }));
          break;

        case 'event': {
          if (msg.seq <= lastSeqRef.current) break;
          lastSeqRef.current = msg.seq;
          const entry: SbxEvent = {
            seq: msg.seq,
            ts: msg.ts,
            layer: msg.layer,
            kind: msg.kind,
            frameUrl: msg.frameUrl,
            data: msg.data,
            artifact: msg.artifact,
          };
          buffer(entry);
          // Latched separately so the ring buffer cannot erase them. Recon
          // streams a growing snapshot per completed check, so "newest wins"
          // is exactly the semantics the panels want.
          if (REPORT_KINDS.has(msg.kind)) {
            setReports((prev) => ({ ...prev, [msg.kind]: msg.data }));
          } else if (DISCOVERY_KINDS.has(msg.kind)) {
            bufferDiscovery(entry);
          }
          break;
        }

        case 'detection':
          setDetections((prev) =>
            prev.some((d) => d.ruleId === msg.ruleId)
              ? prev
              : [
                  ...prev,
                  {
                    ruleId: msg.ruleId,
                    title: msg.title,
                    severity: msg.severity,
                    description: msg.description,
                    evidence: msg.evidence,
                    data: msg.data,
                  },
                ],
          );
          break;

        case 'verdict':
          setVerdict(msg.verdict);
          setScore(msg.score);
          break;

        case 'aiStatus':
          if (msg.state === 'idle') {
            setAiVerdict(null);
            setAiState('idle');
          } else {
            setAiState(msg.state);
          }
          break;

        case 'aiVerdict': {
          setAiState('idle');
          setAiVerdict({
            status: msg.status,
            confidence: msg.confidence,
            summary: msg.summary,
            reasoning: msg.reasoning,
            keySignals: msg.keySignals,
            recommendedAction: msg.recommendedAction,
            model: msg.model,
          });
          // The QR codes the page carried are read off the report once the
          // verdict is in, as the console always read them off the session.
          // Best effort: they are also in the final report, so a read that
          // fails here costs this panel a list and nothing else.
          const id = runRef.current;
          const generation = generationRef.current;
          if (id) {
            api
              .liveReport(id)
              .then((report) => {
                if (generationRef.current !== generation || !Array.isArray(report.qrCodes)) return;
                setQrCodes(
                  report.qrCodes
                    .filter((q): q is Record<string, unknown> => typeof q === 'object' && q !== null)
                    .filter((q) => typeof q.url === 'string')
                    .map((q) => ({
                      url: String(q.url),
                      source: typeof q.source === 'string' ? q.source : '',
                      artifact_type: typeof q.elementType === 'string' ? q.elementType : undefined,
                    })),
                );
              })
              .catch(() => undefined);
          }
          break;
        }

        case 'technologies':
          setTechnologies(msg.data);
          if (msg.icons) setTechIcons(msg.icons);
          break;

        case 'netSummary':
          setNetSummary(msg.data);
          break;

        case 'dialog':
          setDialog({ type: msg.type, message: msg.message, defaultPrompt: msg.defaultPrompt, url: msg.url });
          break;

        case 'error':
          setError(msg.message);
          if (msg.fatal) setConnection('error');
          break;
      }
    },
    [markConnected, buffer, bufferDiscovery],
  );

  /**
   * Decode and paint one frame, with at most one decode in flight.
   *
   * A screencast can deliver frames faster than a busy machine decodes them,
   * and decoding every one queues work whose result is thrown away the moment a
   * newer frame lands. So a frame arriving mid-decode waits - replacing any
   * older one already waiting - and only the newest is decoded next. The canvas
   * is never more than one frame behind the stream, and never behind by a queue.
   */
  const paint = useCallback(
    (first: ScreenFrame) => {
      if (decodingRef.current) {
        waitingFrameRef.current = first;
        return;
      }
      const generation = generationRef.current;
      const decode = (f: ScreenFrame) => {
        decodingRef.current = true;
        createImageBitmap(new Blob([f.jpeg], { type: 'image/jpeg' }))
          .then(
            (bitmap) => {
              if (generation !== generationRef.current) {
                bitmap.close();
                return;
              }
              // The bitmap holds GPU-backed memory that garbage collection
              // reclaims late, so the previous one is closed explicitly rather
              // than left for a live session to accumulate.
              setFrame((prev) => {
                prev?.close();
                return bitmap;
              });
              // The page's own size, which is what input is expressed in; the
              // picture may be a scaled-down copy of it. Kept by identity when
              // it has not changed, so a frame does not re-render the console
              // tree to deliver two numbers that did not move.
              const deviceWidth = f.inputWidth || f.screenWidth;
              const deviceHeight = f.inputHeight || f.screenHeight;
              setFrameMeta((prev) =>
                prev.deviceWidth === deviceWidth && prev.deviceHeight === deviceHeight
                  ? prev
                  : { deviceWidth, deviceHeight },
              );
              markConnected();
            },
            () => {
              // A malformed frame must not tear down the session.
            },
          )
          .finally(() => {
            decodingRef.current = false;
            const next = waitingFrameRef.current;
            waitingFrameRef.current = null;
            if (next && generation === generationRef.current) decode(next);
          });
      };
      decode(first);
    },
    [markConnected],
  );

  const followFeed = useCallback(
    async (id: string, signal: AbortSignal) => {
      let wait = RETRY_MS;
      while (!signal.aborted) {
        const end = await readFeedStream(
          id,
          (m) => {
            markViewBusy('feed', false);
            if (typeof m === 'object' && m !== null && 't' in m) handleMessage(m as ServerMessage);
          },
          signal,
        );
        if (signal.aborted) return;
        markViewBusy('feed', end === 'busy');
        if (end === 'not-applicable') {
          setError('This analysis has no live console.');
          return;
        }
        if (end === 'refused') {
          setError('This analysis can no longer be watched from this session.');
          return;
        }
        // The run is over: its report is where the rest of it is. Anything
        // short of over is worth asking again - a console opened on a QUEUED
        // run is refused until a runner takes it, and giving up there is how a
        // console ends up watching nothing for a run that went on to detonate.
        if (settledRef.current) return;
        // A feed that carried something was working, so the next gap starts
        // from the short wait again; only repeated refusals back off.
        wait = end === 'ended' ? RETRY_MS : Math.min(wait * 2, RETRY_CEILING_MS);
        await delay(wait, signal);
      }
    },
    [handleMessage, markViewBusy],
  );

  const followScreen = useCallback(
    async (id: string, signal: AbortSignal) => {
      let wait = RETRY_MS;
      while (!signal.aborted) {
        const end = await readScreenStream(
          id,
          (f) => {
            markViewBusy('screen', false);
            paint(f);
          },
          signal,
        );
        if (signal.aborted) return;
        markViewBusy('screen', end === 'busy');
        if (end === 'not-applicable' || end === 'refused') return;
        if (settledRef.current) return;
        wait = end === 'ended' ? RETRY_MS : Math.min(wait * 2, RETRY_CEILING_MS);
        await delay(wait, signal);
      }
    },
    [paint, markViewBusy],
  );

  // Open the live views as soon as there is a run to watch, rather than when a
  // status poll happens to observe it RUNNING. The routes themselves say when
  // there is nothing yet - a queued run is refused and the loops above ask
  // again - so the console connects the moment the analyzer advertises itself
  // instead of on the next tick of a clock that can be throttled, paused in a
  // background tab, or land either side of a short run.
  //
  // Not torn down when the run stops: the analyzer's teardown broadcasts the
  // final verdict and network summary on its way out, and the provider ends
  // both streams itself once the run is gone. Only a new run, a reset or
  // leaving the page aborts them.
  useEffect(() => {
    if (!runId || settled || streamsRef.current?.runId === runId) return;
    const ctrl = new AbortController();
    streamsRef.current = { runId, ctrl };
    void followFeed(runId, ctrl.signal);
    void followScreen(runId, ctrl.signal);
  }, [runId, settled, followFeed, followScreen]);

  useEffect(
    () => () => {
      streamsRef.current?.ctrl.abort();
      frameRef.current?.close();
      if (flushTimerRef.current !== null) clearTimeout(flushTimerRef.current);
    },
    [],
  );

  // The connection follows the run: its end closes the console, and a failure
  // says why in the run's own words.
  useEffect(() => {
    if (!status) return;
    if (status === 'failed' || status === 'canceled') {
      if (status === 'canceled' && stopRequestedRef.current) {
        setConnection('closed');
        return;
      }
      setError(failureMessage || (status === 'canceled' ? 'The analysis was cancelled.' : 'The analysis failed.'));
      setConnection('error');
    } else if (status === 'uploading' || status === 'stopping' || status === 'completed') {
      setConnection((c) => (c === 'idle' ? c : 'closed'));
    }
  }, [status, failureMessage]);

  const artifactsQ = useQuery({
    queryKey: ['url-console', 'artifacts', runId],
    queryFn: () => api.artifactList(runId as string),
    enabled: runId !== null && status === 'completed',
  });

  /** Abandon everything shown for the current run. */
  const clearRun = useCallback(() => {
    streamsRef.current?.ctrl.abort();
    streamsRef.current = null;
    generationRef.current += 1;
    lastSeqRef.current = 0;
    stopRequestedRef.current = false;
    setStopRequested(false);
    // Whatever was collected for the previous run is dropped, not flushed into
    // this one.
    pendingRef.current = null;
    if (flushTimerRef.current !== null) {
      clearTimeout(flushTimerRef.current);
      flushTimerRef.current = null;
    }
    waitingFrameRef.current = null;
    setPage(EMPTY_PAGE);
    setEvents([]);
    setReports({});
    setDiscoveries([]);
    setDetections([]);
    setQrCodes([]);
    setVerdict('unknown');
    setScore(0);
    setAiVerdict(null);
    setAiState('idle');
    setTechnologies({});
    setTechIcons({});
    setNetSummary(null);
    setDialog(null);
    setError(null);
    viewBusyRef.current = { screen: false, feed: false };
    setViewLimited(false);
    setFrame((prev) => {
      prev?.close();
      return null;
    });
    setFrameMeta({});
  }, []);

  /**
   * End the run because nobody is watching it any more.
   *
   * A console session is driven, so leaving it ends the analysis: the analyst
   * has seen what they came to see, and a run left behind holds a runner and
   * sits in the submissions list claiming to be in flight for as long as its
   * budget allows. A RUNNING one is finished, which keeps its result and still
   * produces the report; one that never started is cancelled, because there is
   * nothing to keep.
   *
   * `keepalive` rather than the api client: this also fires while the tab is
   * closing, where an ordinary fetch dies with the document.
   */
  const endRunOnLeave = useCallback(() => {
    const id = runRef.current;
    const s = statusRef.current;
    if (!id || stopRequestedRef.current || s === null || SETTLED.has(s)) return;
    stopRequestedRef.current = true;
    const route = s === 'running' ? 'finish' : 'cancel';
    // Nothing reads the answer: the page is going away, and the run's own
    // status is what the submissions list reports either way.
    void fetch(`/api/submissions/${id}/${route}`, {
      method: 'POST',
      credentials: 'include',
      keepalive: true,
    }).catch(() => undefined);
  }, []);

  useEffect(() => {
    // Unmount only. Leaving the console within the app is a decision - the
    // analyst navigated somewhere else - and ending the run there is what the
    // doc above describes.
    //
    // Deliberately NOT pagehide. That event fires on a reload as readily as on
    // a tab closing, and it carries nothing that tells the two apart, so F5
    // ended the analyst's own detonation. This console is built to survive one:
    // it writes the run into `?watch=` and reattaches to a run already in
    // flight, which is exactly the case a reload produces. A closed tab leaves
    // the run to its own budget and the platform's reaper, which is bounded and
    // recoverable; a detonation killed by a refresh is neither.
    return endRunOnLeave;
  }, [endRunOnLeave]);

  const start = useCallback(
    async (opts: SessionOptions = {}) => {
      // One console shows one run. Starting another means this analyst is done
      // with the last one, so it is ended rather than left to hold a runner
      // until its budget runs out.
      endRunOnLeave();
      clearRun();
      runRef.current = null;
      setRunId(null);
      setConnection('starting');
      if (!orgId) {
        setError('Choose an organization to run the analysis in.');
        setConnection('error');
        return;
      }
      try {
        const analyzer = await chooseAnalyzer(qc);
        const res = await api.submitURL({
          org_id: orgId,
          url: opts.url ?? '',
          analyzer: analyzer.name,
          timeout: CONSOLE_TIMEOUT_SECONDS,
          analyzer_options: consoleOptions(opts, analyzer.submittable_options),
          exit_country: opts.exitCountry,
        });
        runRef.current = res.submission_id;
        setRunId(res.submission_id);
        void qc.invalidateQueries({ queryKey: ['submissions'] });
      } catch (err) {
        setError(messageOf(err, 'Could not start the analysis.'));
        setConnection('error');
      }
    },
    [orgId, qc, clearRun, endRunOnLeave],
  );

  const attach = useCallback(
    (submissionId: string) => {
      clearRun();
      runRef.current = submissionId;
      setRunId(submissionId);
      setConnection('starting');
    },
    [clearRun],
  );

  const stop = useCallback(async () => {
    const id = runRef.current;
    if (!id || stopRequestedRef.current || !canDrive) return;
    // Claimed before the status read below, so a second Stop cannot open a
    // second one while this is still deciding which act it is. Both halves move
    // together: the ref gates the send path synchronously and the state drives
    // the phase, and one without the other is a console that either refuses
    // input it should take or offers input it cannot deliver.
    stopRequestedRef.current = true;
    setStopRequested(true);
    const standDown = () => {
      stopRequestedRef.current = false;
      setStopRequested(false);
    };

    // The status chooses between two OPPOSITE acts, so an unknown one is read
    // rather than assumed.
    //
    // Unknown is ordinary here, not a rare race: Stop is offered from the
    // moment there is a run - `attach` marks the connection `starting`
    // synchronously - while the query behind `statusRef` is still out, and
    // `?watch=` attaches to runs that are already detonating.
    let s = statusRef.current;
    if (s === null) {
      try {
        s = (await api.getSubmission(id)).task.status;
      } catch (err) {
        // Deliberately not cancelled on a failed read. Finishing a run that
        // cannot be finished costs a refusal the analyst can act on; discarding
        // one that could have been finished costs the analysis.
        standDown();
        setError(messageOf(err, 'Could not stop the analysis.'));
        return;
      }
    }
    if (s !== 'pending' && s !== 'initializing' && s !== 'running') {
      standDown();
      return;
    }

    try {
      // A running run is finished, which keeps its result: the analyzer tears
      // the session down and the verdict is written as it would be anyway. One
      // that has not started has nothing to keep, so it is cancelled instead.
      if (s === 'running') await api.finishSubmission(id);
      else await api.cancelSubmission(id);
    } catch (err) {
      // 409 is the ordinary race: the run ended on its own between the click
      // and its landing, which is the outcome Stop asks for. The beat stands -
      // taking it back here would resume driving a run that is already over.
      if (err instanceof ApiError && err.status === 409) return;
      // Anything else and the run is still going, so the console has to be
      // drivable again.
      standDown();
      setError(messageOf(err, 'Could not stop the analysis.'));
    }
  }, [canDrive]);

  // The verdict on demand, as the console always ran it: the pass runs while
  // the session goes on, and its outcome arrives both as this answer and on the
  // feed, so a console that stops waiting still sees it land.
  const finish = useCallback(async () => {
    const id = runRef.current;
    if (!id || !canDrive) return;
    const generation = generationRef.current;
    setAiState('analyzing');
    try {
      const { produced } = await api.liveVerdict(id);
      // `produced` is one bit for several outcomes - the pass is switched off,
      // the model failed, the run had nothing to read - and the gateway is the
      // only party that knows which. It says so on the feed as `aiStatus`, so
      // this records the OUTCOME and leaves the cause to whoever knows it.
      //
      // Reading it as "not configured" told operators their deployment had no
      // credential every time a model call failed.
      //
      // The guard is what protects a state the gateway already reported: an
      // `aiStatus` that landed while the request was in flight has moved this
      // off `analyzing` already, and must not be overwritten by a weaker
      // answer.
      if (!produced && generationRef.current === generation) {
        setAiState((s) => (s === 'analyzing' ? 'unproduced' : s));
      }
    } catch {
      if (generationRef.current === generation) setAiState('error');
    }
  }, [canDrive]);

  const reset = useCallback(async () => {
    const s = statusRef.current;
    if (runRef.current && (s === null || !SETTLED.has(s))) await stop();
    clearRun();
    runRef.current = null;
    setRunId(null);
    setConnection('idle');
  }, [stop, clearRun]);

  const send = useCallback((msg: Record<string, unknown>) => {
    const id = runRef.current;
    // Nothing is driven after a stop. The task reports `running` for the
    // seconds it takes the analyzer to tear the session down, and every action
    // delivered into that window is one the provider refuses - a pointer moving
    // over the last frame is enough to send a burst of them.
    if (!id || !canDrive || stopRequestedRef.current || statusRef.current !== 'running') return;
    const action = inputOf(msg);
    if (!action) return;
    const generation = generationRef.current;
    api.sendAnalysisInput(id, action).catch((err: unknown) => {
      if (generationRef.current !== generation) return;
      if (err instanceof ApiError && err.status === 403) {
        setError('The analyzer refused that action.');
      } else if (DISCRETE.has(String(msg.t)) && !(err instanceof ApiError && err.status === 409)) {
        setError(messageOf(err, 'The action could not be delivered.'));
      }
    });
  }, [canDrive]);

  const navigate = useCallback((url: string) => send({ t: 'navigate', url }), [send]);

  const reload = useCallback(() => send({ t: 'reload' }), [send]);

  const answerDialog = useCallback(
    (accept: boolean, promptText = '') => {
      send({ t: 'dialog', accept, promptText });
      setDialog(null);
    },
    [send],
  );

  // While the run is going a file is read from its backend, as the console
  // always read it. Once it is over the session holding it is gone, and the
  // copy the analyzer carried into the result archive is the one to offer.
  const artifacts = artifactsQ.data;
  const artifactUrl = useCallback(
    (sha256: string) => {
      if (!runId) return null;
      if (liveNow) return api.liveArtifactUrl(runId, sha256);
      return artifacts?.find((a) => a.result_name === `artifacts/${sha256}.bin`)?.url ?? null;
    },
    [runId, liveNow, artifacts],
  );

  return {
    connection,
    runId,
    phase: phaseOf(runId, status, stopRequested),
    page,
    events,
    reports,
    discoveries,
    detections,
    qrCodes,
    verdict,
    score,
    aiVerdict,
    aiState,
    technologies,
    techIcons,
    netSummary,
    dialog,
    error,
    viewLimited,
    frame,
    frameMeta,
    start,
    attach,
    stop,
    reset,
    finish,
    navigate,
    reload,
    send,
    answerDialog,
    artifactUrl,
    canDrive,
  };
}
